import { useCallback } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import {
  AppendTranscriptSegmentInput,
  SpeechTokenResponse,
  Transcript,
  TranscriptFolder,
  TranscriptFolderDeletePreview,
  TranscriptFolderDeleteResult,
  TranscriptVisibility,
  TranscriptStatus,
} from "../types/transcripts";

const basePath = (workspaceId: string) =>
  `/api/workspace/meridian/transcripts?workspace_id=${encodeURIComponent(workspaceId)}`;

const pathWithWorkspace = (workspaceId: string, path: string) =>
  `/api/workspace/meridian/transcripts${path}?workspace_id=${encodeURIComponent(workspaceId)}`;

const jsonHeaders = { "Content-Type": "application/json" };

export function useTranscripts() {
  const authFetch = useAuthFetch();
  const authFetchRaw = useAuthFetch("raw");

  const listFolders = useCallback(
    async (workspaceId: string): Promise<TranscriptFolder[]> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, "/folders"),
        "fetch transcript folders",
      )) as TranscriptFolder[],
    [authFetch],
  );

  const createFolder = useCallback(
    async (
      workspaceId: string,
      name: string,
      parentFolderId: number | null = null,
    ): Promise<TranscriptFolder> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, "/folders"),
        "create transcript folder",
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ name, parent_folder_id: parentFolderId }),
        },
      )) as TranscriptFolder,
    [authFetch],
  );

  const updateFolder = useCallback(
    async (
      workspaceId: string,
      folderId: number,
      payload: { name?: string; parent_folder_id?: number | null },
    ): Promise<TranscriptFolder> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, `/folders/${folderId}`),
        "update transcript folder",
        {
          method: "PATCH",
          headers: jsonHeaders,
          body: JSON.stringify(payload),
        },
      )) as TranscriptFolder,
    [authFetch],
  );

  const deleteFolder = useCallback(
    async (
      workspaceId: string,
      folderId: number,
    ): Promise<TranscriptFolderDeleteResult> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, `/folders/${folderId}`),
        "delete transcript folder",
        {
          method: "DELETE",
        },
      )) as TranscriptFolderDeleteResult,
    [authFetch],
  );

  const previewDeleteFolder = useCallback(
    async (
      workspaceId: string,
      folderId: number,
    ): Promise<TranscriptFolderDeletePreview> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, `/folders/${folderId}/delete-preview`),
        "preview transcript folder delete",
      )) as TranscriptFolderDeletePreview,
    [authFetch],
  );

  const listTranscripts = useCallback(
    async (
      workspaceId: string,
      folderId?: number | null,
      status?: TranscriptStatus,
      q?: string,
    ): Promise<Transcript[]> => {
      const suffix =
        folderId == null
          ? ""
          : `&folder_id=${encodeURIComponent(String(folderId))}`;
      const statusSuffix = status
        ? `&status=${encodeURIComponent(status)}`
        : "";
      const querySuffix = q ? `&q=${encodeURIComponent(q)}` : "";
      const pageSize = 100;
      const transcripts: Transcript[] = [];
      let offset = 0;
      let totalCount: number | null = null;

      while (true) {
        const response = (await authFetchRaw(
          `${basePath(workspaceId)}${suffix}${statusSuffix}${querySuffix}&limit=${pageSize}&offset=${offset}`,
          "fetch transcripts",
        )) as Response;
        const page = (await response.json()) as Transcript[];
        transcripts.push(...page);

        const rawTotal = response.headers.get("X-Total-Count");
        if (rawTotal !== null && rawTotal.trim() !== "") {
          const responseTotal = Number(rawTotal);
          if (Number.isSafeInteger(responseTotal) && responseTotal >= 0) {
            totalCount = responseTotal;
          }
        }

        if (
          page.length === 0 ||
          (totalCount !== null
            ? transcripts.length >= totalCount
            : page.length < pageSize)
        ) {
          return transcripts;
        }
        offset += page.length;
      }
    },
    [authFetchRaw],
  );

  const createTranscript = useCallback(
    async (
      workspaceId: string,
      payload: { title: string; language: string; folder_id?: number | null },
    ): Promise<Transcript> =>
      (await authFetch(basePath(workspaceId), "create transcript", {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify(payload),
      })) as Transcript,
    [authFetch],
  );

  const getTranscript = useCallback(
    async (workspaceId: string, transcriptId: number): Promise<Transcript> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, `/${transcriptId}`),
        "fetch transcript",
      )) as Transcript,
    [authFetch],
  );

  const updateTranscript = useCallback(
    async (
      workspaceId: string,
      transcriptId: number,
      payload: {
        title?: string;
        folder_id?: number | null;
        transcript_text?: string;
      },
    ): Promise<Transcript> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, `/${transcriptId}`),
        "update transcript",
        {
          method: "PATCH",
          headers: jsonHeaders,
          body: JSON.stringify(payload),
        },
      )) as Transcript,
    [authFetch],
  );

  const deleteTranscript = useCallback(
    async (workspaceId: string, transcriptId: number): Promise<void> => {
      await authFetch(
        pathWithWorkspace(workspaceId, `/${transcriptId}`),
        "delete transcript",
        {
          method: "DELETE",
        },
      );
    },
    [authFetch],
  );

  const getSpeechToken = useCallback(
    async (
      workspaceId: string,
      transcriptId: number,
    ): Promise<SpeechTokenResponse> =>
      (await authFetch(
        `${pathWithWorkspace(workspaceId, "/speech-token")}&transcript_id=${encodeURIComponent(String(transcriptId))}`,
        "fetch speech token",
      )) as SpeechTokenResponse,
    [authFetch],
  );

  const appendSegment = useCallback(
    async (
      workspaceId: string,
      transcriptId: number,
      payload: AppendTranscriptSegmentInput,
    ) =>
      await authFetch(
        pathWithWorkspace(workspaceId, `/${transcriptId}/segments`),
        "save transcript segment",
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(payload),
        },
      ),
    [authFetch],
  );

  const completeTranscript = useCallback(
    async (
      workspaceId: string,
      transcriptId: number,
      payload: { transcript_text?: string; duration_seconds?: number },
    ): Promise<Transcript> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, `/${transcriptId}/complete`),
        "complete transcript",
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(payload),
        },
      )) as Transcript,
    [authFetch],
  );

  const updateVisibility = useCallback(
    async (
      workspaceId: string,
      transcriptId: number,
      visibility: TranscriptVisibility,
      confirmPublicToWorkspace = false,
    ): Promise<Transcript> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, `/${transcriptId}/visibility`),
        "update transcript visibility",
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({
            visibility,
            confirm_public_to_workspace: confirmPublicToWorkspace,
          }),
        },
      )) as Transcript,
    [authFetch],
  );

  const updateBatchVisibility = useCallback(
    async (
      workspaceId: string,
      transcriptIds: number[],
      visibility: TranscriptVisibility,
      confirmPublicToWorkspace = false,
    ): Promise<Transcript[]> =>
      (await authFetch(
        pathWithWorkspace(workspaceId, "/visibility/batch"),
        "update transcript visibility batch",
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({
            transcript_ids: transcriptIds,
            visibility,
            confirm_public_to_workspace: confirmPublicToWorkspace,
          }),
        },
      )) as Transcript[],
    [authFetch],
  );

  const downloadTranscript = useCallback(
    async (workspaceId: string, transcriptId: number, title: string) => {
      const response = (await authFetchRaw(
        pathWithWorkspace(workspaceId, `/${transcriptId}/download.txt`),
        "download transcript",
      )) as Response;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title.trim().replace(/[^A-Za-z0-9._-]+/g, "_") || "transcript"}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    [authFetchRaw],
  );

  return {
    appendSegment,
    completeTranscript,
    createFolder,
    createTranscript,
    deleteFolder,
    deleteTranscript,
    downloadTranscript,
    getSpeechToken,
    getTranscript,
    listFolders,
    listTranscripts,
    previewDeleteFolder,
    updateBatchVisibility,
    updateFolder,
    updateTranscript,
    updateVisibility,
  };
}
